import type { ReactNode } from 'react'

/**
 * Form controls, from Tailwind UI's forms/input-groups and select-menus, on
 * shell-* tokens. Inline-label-free by default: these sit in compact toolbars
 * where a visible label would double the height, so the label is attached for
 * assistive tech and shown only when `label` is given.
 */

const CONTROL =
  'block w-full rounded-md bg-shell-surface px-3 py-1.5 text-sm text-shell-ink outline-1 -outline-offset-1 outline-shell-border placeholder:text-shell-ink-muted focus:outline-2 focus:-outline-offset-2 focus:outline-shell-accent'

type TextInputProps = {
  name: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  /** Datalist id for suggestions, when the caller renders one. */
  list?: string
  type?: 'text' | 'email' | 'password' | 'date'
  /**
   * Tells the browser which credential to offer — 'current-password' offers the
   * saved one, 'new-password' offers a generated one. Omitting it on a
   * credential field is what the "[DOM] Input elements should have autocomplete
   * attributes" warning is about.
   */
  autoComplete?: string
  className?: string
}

export const TextInput = ({
  name,
  value,
  onChange,
  placeholder,
  label,
  list,
  type = 'text',
  autoComplete,
  className = '',
}: TextInputProps) => (
  <div className={className}>
    {label && (
      <label htmlFor={name} className="mb-1 block text-sm font-medium text-shell-ink">
        {label}
      </label>
    )}
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      aria-label={label ? undefined : placeholder}
      placeholder={placeholder}
      list={list}
      autoComplete={autoComplete}
      onInput={(event) => onChange(event.currentTarget.value)}
      className={CONTROL}
    />
  </div>
)

type TextareaProps = {
  name: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  rows?: number
  className?: string
}

export const Textarea = ({ name, value, onChange, placeholder, label, rows = 4, className = '' }: TextareaProps) => (
  <div className={className}>
    {label && (
      <label htmlFor={name} className="mb-1 block text-sm font-medium text-shell-ink">
        {label}
      </label>
    )}
    <textarea
      id={name}
      name={name}
      value={value}
      aria-label={label ? undefined : placeholder}
      placeholder={placeholder}
      rows={rows}
      onInput={(event) => onChange(event.currentTarget.value)}
      className={CONTROL}
    />
  </div>
)

/**
 * Suggestions for a `TextInput` that names one, via its `list` prop.
 *
 * The browser's own datalist rather than a hand-built combobox: it narrows as you
 * type, is keyboard-navigable and screen-reader-announced without any code of
 * ours, and — the part that matters here — it stays a text field, so a value that
 * is not in the list can still be typed. Every place this is used has to accept
 * that: you must be able to share with someone you have not met yet.
 *
 * `id` must be unique in the document. Callers name theirs after their surface
 * (`people-my-teams`), so two lists on one page cannot collide.
 */
export const Datalist = ({ id, options }: { id: string; options: readonly string[] }) => (
  <datalist id={id}>
    {options.map((option) => (
      <option key={option} value={option} />
    ))}
  </datalist>
)

type SelectProps = {
  name: string
  value: string
  onChange: (value: string) => void
  label?: string
  /** Rendered first, for the "no choice / default" case. */
  placeholderOption?: string
  options: readonly { value: string; label: string }[]
  className?: string
}

export const Select = ({ name, value, onChange, label, placeholderOption, options, className = '' }: SelectProps) => (
  <div className={className}>
    {label && (
      <label htmlFor={name} className="mb-1 block text-sm font-medium text-shell-ink">
        {label}
      </label>
    )}
    <select
      id={name}
      name={name}
      value={value}
      aria-label={label ?? placeholderOption}
      onChange={(event) => onChange(event.currentTarget.value)}
      className={`${CONTROL} pr-8`}
    >
      {placeholderOption !== undefined && <option value="">{placeholderOption}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
)

/**
 * Checkbox with its label, per forms/checkboxes.
 *
 * `hideLabel` keeps the label for assistive tech and drops it visually — for the
 * one in a table cell, where the column heading already says what it does and
 * repeating it on every row would be a wall of text down the side of the table.
 * The label is never *absent*: a bare checkbox is announced as nothing at all.
 */
export const Checkbox = ({
  name,
  checked,
  onChange,
  label,
  hideLabel = false,
  disabled = false,
}: {
  name: string
  checked: boolean
  onChange: () => void
  label: string
  hideLabel?: boolean
  disabled?: boolean
}) => (
  <label className="flex items-center gap-x-2 text-sm text-shell-ink">
    <input
      name={name}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className="size-4 rounded border-shell-border text-shell-accent focus:ring-shell-accent disabled:opacity-50"
    />
    {hideLabel ? <span className="sr-only">{label}</span> : label}
  </label>
)

export const RadioOption = ({
  name,
  checked,
  onChange,
  label,
}: {
  name: string
  checked: boolean
  onChange: () => void
  label: string
}) => (
  <label className="flex items-center gap-x-2 text-sm text-shell-ink">
    <input
      name={name}
      type="radio"
      checked={checked}
      onChange={onChange}
      className="size-4 border-shell-border text-shell-accent focus:ring-shell-accent"
    />
    {label}
  </label>
)

/**
 * A row of controls that submits as one — the inline "add X" bar.
 *
 * `busy` marks the whole form as working while its write is in flight. The
 * disabled button and its changed label say so visually; `aria-busy` is how a
 * screen reader learns the same thing.
 */
export const InlineForm = ({
  onSubmit,
  busy = false,
  children,
}: {
  onSubmit: () => void
  busy?: boolean
  children: ReactNode
}) => (
  <form
    aria-busy={busy || undefined}
    className="flex flex-wrap items-end gap-3"
    onSubmit={(event) => {
      event.preventDefault()
      onSubmit()
    }}
  >
    {children}
  </form>
)
